/**
 * Test Voice Modal
 *
 * Modal for testing TTS provider voices with custom or standard sample text.
 * Features audio generation, playback, and download.
 *
 * Acceptance Criteria: AC 27, 34
 */

import { useState, useEffect, useMemo } from 'react';
import { useTestVoice, type TTSProvider } from '../services/voiceApi';
import { useInitialData } from 'contexts/InitialDataContext';

interface TestVoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: TTSProvider;
  providerVoiceId: string;
  voiceName: string;
  languages?: string[];
}

// Standard sample texts by language
const STANDARD_SAMPLES: Record<string, string> = {
  en: 'Hello! This is a test of the text-to-speech system. The quick brown fox jumps over the lazy dog.',
  es: 'Hola! Esta es una prueba del sistema de texto a voz. El rápido zorro marrón salta sobre el perro perezoso.',
  fr: 'Bonjour! Ceci est un test du système de synthèse vocale. Le rapide renard brun saute par-dessus le chien paresseux.',
  de: 'Hallo! Dies ist ein Test des Text-zu-Sprache-Systems. Der schnelle braune Fuchs springt über den faulen Hund.',
};

export function TestVoiceModal({
  isOpen,
  onClose,
  provider,
  providerVoiceId,
  voiceName,
  languages = [],
}: TestVoiceModalProps) {
  const { state: initialDataState } = useInitialData();

  // Create language code to name mapping from initial data
  const languageMap = useMemo(() => {
    if (!initialDataState.data?.languages) return new Map<string, string>();
    return new Map(initialDataState.data.languages.map((lang) => [lang.code, lang.name]));
  }, [initialDataState.data?.languages]);

  // Helper function to get language display name
  const getLanguageName = (code: string): string => {
    return languageMap.get(code) || code.toUpperCase();
  };

  // Determine available languages: use TTS languages if provided, otherwise show all samples
  const availableLanguages =
    languages.length > 0
      ? languages.filter((lang) => STANDARD_SAMPLES[lang])
      : Object.keys(STANDARD_SAMPLES);

  const [useStandardSample, setUseStandardSample] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState(
    availableLanguages.length > 0 ? availableLanguages[0] : 'en'
  );
  const [customText, setCustomText] = useState('');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const testVoiceMutation = useTestVoice();

  const text = useStandardSample ? STANDARD_SAMPLES[selectedLanguage] : customText;
  const charCount = text.length;
  const maxChars = 500;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setUseStandardSample(true);
      setSelectedLanguage(availableLanguages.length > 0 ? availableLanguages[0] : 'en');
      setCustomText('');
      setAudioUrl(null);
      setError(null);
    }
  }, [isOpen, availableLanguages]);

  const handleGenerateAudio = async () => {
    setError(null);
    setAudioUrl(null);

    if (!text.trim()) {
      setError('Text is required');
      return;
    }

    if (text.length > maxChars) {
      setError(`Text must be ${maxChars} characters or less`);
      return;
    }

    try {
      const response = await testVoiceMutation.mutateAsync({
        providerId: provider.id,
        request: {
          providerVoiceId,
          text: text.trim(),
        },
      });

      setAudioUrl(response.audioUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate audio');
    }
  };

  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `${voiceName}-sample.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="test-voice-modal"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold" data-testid="modal-header">
              Test Voice: {voiceName}
            </h2>
            <p className="text-sm text-gray-600">{provider.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            data-testid="close-button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Text Source Toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Text Source</label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={useStandardSample}
                onChange={() => setUseStandardSample(true)}
                className="mr-2"
                data-testid="standard-sample-radio"
              />
              <span>Standard Sample</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={!useStandardSample}
                onChange={() => setUseStandardSample(false)}
                className="mr-2"
                data-testid="custom-text-radio"
              />
              <span>Custom Text</span>
            </label>
          </div>
        </div>

        {/* Language Selector (for standard samples) */}
        {useStandardSample && availableLanguages.length > 0 && (
          <div className="mb-4">
            <label
              htmlFor="language-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Sample Language
            </label>
            <select
              id="language-select"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="language-select"
            >
              {availableLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {getLanguageName(lang)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Text Input */}
        <div className="mb-4">
          <label htmlFor="text-input" className="block text-sm font-medium text-gray-700 mb-2">
            {useStandardSample ? 'Sample Text (Preview)' : 'Custom Text'}
          </label>
          <textarea
            id="text-input"
            value={text}
            onChange={(e) => !useStandardSample && setCustomText(e.target.value)}
            disabled={useStandardSample}
            rows={4}
            maxLength={maxChars}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              useStandardSample ? 'bg-gray-50' : ''
            }`}
            placeholder={useStandardSample ? '' : 'Enter text to convert to speech...'}
            data-testid="text-input"
          />
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">
              {useStandardSample ? 'Standard sample text' : 'Type your custom text'}
            </span>
            <span
              className={`text-xs ${charCount > maxChars ? 'text-red-600' : 'text-gray-500'}`}
              data-testid="character-counter"
            >
              {charCount} / {maxChars}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded"
            data-testid="error-message"
          >
            {error}
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={handleGenerateAudio}
          disabled={testVoiceMutation.isPending || charCount === 0 || charCount > maxChars}
          className="w-full mb-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
          data-testid="generate-audio-button"
        >
          {testVoiceMutation.isPending ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </span>
          ) : (
            'Generate Audio'
          )}
        </button>

        {/* Audio Player */}
        {audioUrl && (
          <div
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
            data-testid="audio-player-container"
          >
            <p className="text-sm font-medium text-gray-700 mb-2">Generated Audio:</p>
            <audio controls className="w-full mb-3" data-testid="audio-player">
              <source src={audioUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
            <button
              onClick={handleDownload}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              data-testid="download-button"
            >
              Download Audio
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
